"use client";

import { c as _c } from "react/compiler-runtime";
import { OrgPhoto } from "@/components/ui/OrgPhoto";
import { WorkflowCardIconStrip } from "@/components/workflow/WorkflowCardIconStrip";
import { useActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import { stripMarkdown } from "@/lib/templates/stripMarkdown";
import { getTemplateUrl } from "@/lib/templates/templateUrl";
import type { Template } from "@/stores/templatesStore";
import { IconBuildings } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import Link from "next/link";
import { useMemo } from "react";
import { buildCompactWorkflowData, CompactWorkflowSteps } from "./CompactWorkflowSteps";
interface PublicTemplateCardProps {
  template: Template;
  variant?: "default" | "featured";
}
const TAG_PALETTES = [{
  bg: "#e8efff",
  border: "#b3c8ff",
  text: "#2450ff"
}, {
  bg: "#fefce8",
  border: "#fef08a",
  text: "#a16207"
}, {
  bg: "#ecfdf5",
  border: "#a7f3d0",
  text: "#047857"
}] as const;
function FeaturedTemplateHeader(t0) {
  const $ = _c(6);
  const {
    template
  } = t0;
  const {
    triggers
  } = useTriggers();
  const {
    actions
  } = useActions();
  let t1;
  let t2;
  if ($[0] !== actions || $[1] !== template.definition || $[2] !== triggers) {
    t2 = buildCompactWorkflowData(template.definition, triggers, actions, {
      maxFlowSteps: 7,
      showOverflowRow: false
    });
    $[0] = actions;
    $[1] = template.definition;
    $[2] = triggers;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  t1 = t2;
  const compactData = t1;
  let t3;
  if ($[4] !== compactData) {
    t3 = <CompactWorkflowSteps data={compactData} className="h-[277px]" />;
    $[4] = compactData;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function TemplateIconHeader(t0) {
  const $ = _c(7);
  const {
    template
  } = t0;
  let t1;
  if ($[0] !== template.description || $[1] !== template.id || $[2] !== template.name) {
    t1 = {
      id: template.id,
      name: template.name,
      description: template.description
    };
    $[0] = template.description;
    $[1] = template.id;
    $[2] = template.name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== template) {
    t2 = <div className="bg-ds-surface-muted border-ds-border relative flex h-[105px] w-full items-center justify-center overflow-hidden rounded-md border"><WorkflowCardIconStrip source={template} fallback={t1} /></div>;
    $[4] = t1;
    $[5] = template;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function PublicTemplateCard(t0) {
  const $ = _c(49);
  const {
    template,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  let T0;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== template || $[1] !== variant) {
    const categoryTags = template.tags?.filter(_temp) || [];
    T0 = Link;
    if ($[12] !== template) {
      t7 = getTemplateUrl(template);
      $[12] = template;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t8 = "border-ds-border bg-ds-surface-card hover:border-ds-text-disabled dark:hover:border-ds-neutral-700 hover:shadow-ds-elev-3 group flex cursor-pointer flex-col overflow-hidden rounded-xl border p-2 transition-all";
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        boxShadow: "0px 1px 2px 0px rgba(23,22,25,0.05)"
      };
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    t10 = variant === "featured" ? <FeaturedTemplateHeader template={template} /> : <TemplateIconHeader template={template} />;
    t5 = "flex flex-1 flex-col gap-2 pt-5";
    let t11;
    if ($[15] !== template.name) {
      t11 = <h3 className="text-content-secondary leading-label line-clamp-1 text-lg font-medium">{template.name}</h3>;
      $[15] = template.name;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== template.description) {
      t12 = stripMarkdown(template.description);
      $[17] = template.description;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    let t13;
    if ($[19] !== t12) {
      t13 = <p className="text-ds-text-subtle leading-label line-clamp-2 pt-1 text-sm">{t12}</p>;
      $[19] = t12;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    if ($[21] !== t11 || $[22] !== t13) {
      t6 = <div className="flex flex-1 flex-col px-3.5">{t11}{t13}</div>;
      $[21] = t11;
      $[22] = t13;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    t2 = "mt-auto flex items-center justify-between gap-2 px-3.5 pb-2 pt-2";
    let t14;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span className="text-ds-text-muted leading-18 shrink-0 text-xs">By</span>;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    const t15 = template.organizationName || "";
    let t16;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="bg-ds-surface-muted flex h-4 w-4 shrink-0 items-center justify-center rounded-full"><IconBuildings className="text-ds-text-muted h-2.5 w-2.5" /></div>;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    let t17;
    if ($[26] !== t15 || $[27] !== template.organizationPhoto) {
      t17 = <OrgPhoto src={template.organizationPhoto} alt={t15} size={16} rounded="full" className="border border-black/25" fallback={t16} />;
      $[26] = t15;
      $[27] = template.organizationPhoto;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    const t18 = template.organizationName || template.createdBy || "B3";
    let t19;
    if ($[29] !== t18) {
      t19 = <span className="text-ds-text-tertiary leading-18 truncate text-xs">{t18}</span>;
      $[29] = t18;
      $[30] = t19;
    } else {
      t19 = $[30];
    }
    if ($[31] !== t17 || $[32] !== t19) {
      t3 = <div className="flex min-w-0 items-center gap-1">{t14}{t17}{t19}</div>;
      $[31] = t17;
      $[32] = t19;
      $[33] = t3;
    } else {
      t3 = $[33];
    }
    t4 = categoryTags.length > 0 && <div className="flex shrink-0 items-center gap-1">{categoryTags.slice(0, 2).map(_temp2)}{categoryTags.length > 2 && <span className="bg-ds-surface-card text-ds-text-secondary border-ds-border text-2xs leading-18 flex items-center rounded-full border px-1.5 py-px font-medium">+{categoryTags.length - 2}</span>}</div>;
    $[0] = template;
    $[1] = variant;
    $[2] = T0;
    $[3] = t10;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    T0 = $[2];
    t10 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t11;
  if ($[34] !== t2 || $[35] !== t3 || $[36] !== t4) {
    t11 = <div className={t2}>{t3}{t4}</div>;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t11 || $[39] !== t5 || $[40] !== t6) {
    t12 = <div className={t5}>{t6}{t11}</div>;
    $[38] = t11;
    $[39] = t5;
    $[40] = t6;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== T0 || $[43] !== t10 || $[44] !== t12 || $[45] !== t7 || $[46] !== t8 || $[47] !== t9) {
    t13 = <T0 href={t7} className={t8} style={t9}>{t10}{t12}</T0>;
    $[42] = T0;
    $[43] = t10;
    $[44] = t12;
    $[45] = t7;
    $[46] = t8;
    $[47] = t9;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  return t13;
}
function _temp2(tag_0, i) {
  const palette = TAG_PALETTES[i % TAG_PALETTES.length];
  return <span key={tag_0.id} className="text-2xs leading-18 flex items-center rounded-full border px-1.5 py-px font-medium" style={{
    backgroundColor: palette.bg,
    borderColor: palette.border,
    color: palette.text
  }}>{tag_0.name}</span>;
}
function _temp(tag) {
  return !tag.imageUrl;
}